/**
 * Updated Deepgram service using backend WebSocket proxy
 * Properly handles interim and final results
 */

import { io, Socket } from "socket.io-client";

interface DeepgramConfig {
  apiKey?: string; // Not used with proxy
  model?: string;
  language?: string;
  punctuate?: boolean;
  interim_results?: boolean;
  endpointing?: number;
  utterance_end_ms?: number;
  vad_events?: boolean;
}

export class DeepgramService {
  private socket: Socket | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioStream: MediaStream | null = null;
  private isConnected: boolean = false;
  private isRecording: boolean = false;
  private onTranscriptCallback:
    | ((transcript: string, isFinal: boolean) => void)
    | null = null;
  private onErrorCallback?: ((error: Error) => void) | null = null;
  private onUtteranceEndCallback?: (() => void) | null = null;
  private onSpeechStartedCallback?: (() => void) | null = null;

  /**
   * Connect to Deepgram via backend proxy
   */
  async connect(
    config: DeepgramConfig,
    onTranscript: (transcript: string, isFinal: boolean) => void,
    onError?: (error: Error) => void,
    onUtteranceEnd?: () => void,
    onSpeechStarted?: () => void,
    deviceId?: string,
  ): Promise<void> {
    try {
      console.log("DeepgramService: Starting connection...");

      // Store callbacks
      this.onTranscriptCallback = onTranscript;
      this.onErrorCallback = onError;
      this.onUtteranceEndCallback = onUtteranceEnd;
      this.onSpeechStartedCallback = onSpeechStarted;

      // Get microphone access with optimized settings
      const audioConstraints: MediaTrackConstraints = {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };

      // Add device ID constraint if specified
      if (deviceId && deviceId !== "default") {
        audioConstraints.deviceId = { exact: deviceId };
      }

      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });

      console.log("DeepgramService: Microphone access granted");

      // Connect to backend Deepgram proxy
      const wsUrl = import.meta.env.VITE_WS_URL || "http://localhost:3001";
      this.socket = io(`${wsUrl}/deepgram-proxy`, {
        transports: ["websocket", "polling"],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
      });

      // Set up event handlers
      this.socket.on("connect", () => {
        console.log("DeepgramService: Connected to proxy");
        this.isConnected = true;

        // Start recognition with config
        console.log(
          "DeepgramService: Starting recognition with config:",
          config,
        );
        this.socket?.emit("start-recognition", {
          model: config.model || "nova-3",
          language: config.language || "en-US",
          vad_events: config.vad_events !== false,
        });
      });

      this.socket.on("recognition-started", () => {
        console.log("DeepgramService: Recognition started by server");
        this.startAudioCapture();
      });

      this.socket.on(
        "transcript",
        (data: {
          transcript: string;
          is_final: boolean;
          confidence: number;
        }) => {
          if (data.transcript && data.transcript.trim()) {
            console.log("DeepgramService: Transcript received:", {
              text: data.transcript.trim(),
              isFinal: data.is_final,
              confidence: data.confidence,
            });

            // Call the callback
            this.onTranscriptCallback?.(data.transcript.trim(), data.is_final);
          }
        },
      );

      this.socket.on("error", (data: { message: string }) => {
        console.error("DeepgramService: Proxy error:", data.message);
        this.onErrorCallback?.(new Error(data.message));
      });

      this.socket.on("recognition-ended", () => {
        console.log("DeepgramService: Recognition ended by server");
        this.isRecording = false;
      });

      this.socket.on("utterance-end", () => {
        console.log("DeepgramService: Utterance ended");
        onUtteranceEnd?.(); // Call the callback if provided
      });

      this.socket.on("speech-started", () => {
        console.log("DeepgramService: Speech started");
        this.onSpeechStartedCallback?.();
      });

      this.socket.on("disconnect", (reason) => {
        console.log("DeepgramService: Disconnected from proxy:", reason);
        this.isConnected = false;
        this.isRecording = false;
      });

      this.socket.on("connect_error", (error) => {
        console.error("DeepgramService: Connection error:", error);
        this.onErrorCallback?.(
          new Error(`Connection failed: ${error.message}`),
        );
      });
    } catch (error: any) {
      console.error("DeepgramService: Failed to initialize:", error);
      this.onErrorCallback?.(error);
      throw error;
    }
  }

  /**
   * Start capturing and sending audio to proxy
   */
  private startAudioCapture(): void {
    if (!this.audioStream || !this.socket || !this.isConnected) {
      console.error(
        "DeepgramService: Cannot start audio capture - missing requirements",
      );
      return;
    }

    try {
      const mimeType = this.getSupportedMimeType();
      console.log(
        "DeepgramService: Starting audio capture with MIME type:",
        mimeType,
      );

      this.mediaRecorder = new MediaRecorder(this.audioStream, {
        mimeType,
        bitsPerSecond: 16000,
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0 && this.socket?.connected) {
          // Send audio data to backend proxy
          this.socket.emit("audio-data", event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error("DeepgramService: MediaRecorder error:", event);
        this.onErrorCallback?.(new Error("MediaRecorder error"));
      };

      this.mediaRecorder.onstart = () => {
        this.isRecording = true;
        console.log("DeepgramService: Audio recording started");
      };

      this.mediaRecorder.onstop = () => {
        this.isRecording = false;
        console.log("DeepgramService: Audio recording stopped");
      };

      // Start recording with frequent chunks for lower latency
      this.mediaRecorder.start(50);
    } catch (error) {
      console.error("DeepgramService: Failed to start audio capture:", error);
      this.onErrorCallback?.(new Error("Failed to start audio capture"));
    }
  }

  /**
   * Get supported MIME type for MediaRecorder
   */
  private getSupportedMimeType(): string {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/ogg",
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        console.log("DeepgramService: Using MIME type:", type);
        return type;
      }
    }

    console.warn(
      "DeepgramService: No supported MIME type found, using default",
    );
    return "";
  }

  /**
   * Disconnect from Deepgram proxy
   */
  disconnect(): void {
    console.log("DeepgramService: Starting disconnect...");

    // Stop media recorder
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      try {
        this.mediaRecorder.stop();
      } catch (error) {
        console.warn("DeepgramService: Error stopping MediaRecorder:", error);
      }
      this.mediaRecorder = null;
    }

    // Stop recognition
    if (this.socket && this.socket.connected) {
      try {
        this.socket.emit("stop-recognition");
        this.socket.disconnect();
      } catch (error) {
        console.warn("DeepgramService: Error disconnecting socket:", error);
      }
      this.socket = null;
    }

    // Stop audio tracks
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => {
        track.stop();
        console.log("DeepgramService: Stopped audio track");
      });
      this.audioStream = null;
    }

    // Clear callbacks
    this.onTranscriptCallback = null;
    this.onErrorCallback = null;
    this.onUtteranceEndCallback = null;
    this.onSpeechStartedCallback = null;

    this.isConnected = false;
    this.isRecording = false;

    console.log("DeepgramService: Disconnect completed");
  }

  /**
   * Check if connected and recording
   */
  isActive(): boolean {
    return this.isConnected && this.isRecording;
  }

  /**
   * Pause recording (for temporary silence)
   */
  pause(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.pause();
      console.log("DeepgramService: Recording paused");
    }
  }

  /**
   * Resume recording
   */
  resume(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === "paused") {
      this.mediaRecorder.resume();
      console.log("DeepgramService: Recording resumed");
    }
  }
}

// Export singleton instance
export const deepgramService = new DeepgramService();
